import { Text } from "@growtherapy/sprout-ui";
import {
  ClientInformationSectionWrapper as ClientInformationSectionConnected,
  ClientInformationSectionWrapper,
} from "./ClientInformationSectionWrapper";
import {
  ClientActivityBetweenSessionCardWrapper as ClientInformationSessionsConnected,
  ClientActivityBetweenSessionCardWrapper,
} from "./ClientActivityBetweenSessionCardWrapper";
import { ProviderResourcesCollapsibleContainerWrapper as ProviderResourcesCollapsibleContainer } from "./ProviderResourcesCollapsibleContainerWrapper";
import { ProgressNoteSectionWrapper } from "./ProgressNoteSection";
import { CLIENT_HAS_LEFT_MESSAGE } from "./constants";
import { useAtomValue } from "jotai";
import { AppointmentType } from "../schedule-preview/types";
import { hasNewFormsAtomFamily } from "./state";

import classNames from "classnames";
import { ComponentProps, FC } from "react";
import { useSendClientInfoTabViewTrackingEvent } from "./useSendClientInfoTabViewTrackingEvent";
import { EventTypeEnum } from "../../segment/types";
import { getAppointmentType } from "./utils";
import { selectedClientInfoAtom } from "../state";
import { PreVisitSummaryCardWrapper } from "./pre-visit-summary/PreVisitSummaryCardWrapper";
import { useFeatureGate } from "@statsig/react-bindings";
import { preVisitSummaryEnabledGate } from "../../statsig/gates";
import { noop } from "../../utils";

export interface ClientInformationDrawerBodyProps {
  isEmpty: boolean;
  hasNewForms?: boolean;
  isFollowUpAppointment?: boolean;
  progressNoteSectionComponent: FC<
    ComponentProps<typeof ProgressNoteSectionWrapper>
  >;
  clientInformationSectionComponent: FC<
    ComponentProps<typeof ClientInformationSectionWrapper>
  >;
  clientActivityBetweenSessionsComponent: FC<
    ComponentProps<typeof ClientActivityBetweenSessionCardWrapper>
  >;
  providerResourcesCollapsibleContainerComponent: FC;
  preVisitSummaryCardComponent: FC<
    ComponentProps<typeof PreVisitSummaryCardWrapper>
  >;
}

export function ClientInformationTab({
  isEmpty,
  isFollowUpAppointment,
  progressNoteSectionComponent: ProgressNoteSectionComponent,
  clientInformationSectionComponent: ClientInformationSectionComponentConnected,
  clientActivityBetweenSessionsComponent:
    ClientActivityBetweenSessionsComponentConnected,
  providerResourcesCollapsibleContainerComponent:
    ProviderResourcesCollapsibleContainerComponent,
  preVisitSummaryCardComponent: PreVisitSummaryCardComponent,
}: ClientInformationDrawerBodyProps) {
  return (
    <>
      {isEmpty ? (
        <div className="flex flex-col h-full ">
          <div className="pt-6 mt-auto">
            <Text variant="xs" className="text-neutral-700 text-center">
              {CLIENT_HAS_LEFT_MESSAGE}
            </Text>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <PreVisitSummaryCardComponent />
          <ClientActivityBetweenSessionsComponentConnected />
          <ProviderResourcesCollapsibleContainerComponent />
          <ClientInformationSectionComponentConnected />
          <ProgressNoteSectionComponent
            className={classNames({ "order-1 mb-6": isFollowUpAppointment })}
          />
        </div>
      )}
    </>
  );
}

export function ClientInformationTabWrapper({ isEmpty }: { isEmpty: boolean }) {
  const selectedClient = useAtomValue(selectedClientInfoAtom);
  const patientShortId = selectedClient?.patientShortId;
  const { value: isPreVisitSummaryEnabled } = useFeatureGate(
    preVisitSummaryEnabledGate,
  );
  const isCABSCardMovedToClientActivityTab = isPreVisitSummaryEnabled;

  const hasNewForms = useAtomValue(hasNewFormsAtomFamily(patientShortId ?? ""));

  useSendClientInfoTabViewTrackingEvent(
    patientShortId ?? "",
    EventTypeEnum.CLIENT_INFO,
  );

  return (
    <ClientInformationTab
      isEmpty={isEmpty}
      hasNewForms={hasNewForms}
      isFollowUpAppointment={getAppointmentType() === AppointmentType.FollowUp}
      progressNoteSectionComponent={ProgressNoteSectionWrapper}
      clientInformationSectionComponent={ClientInformationSectionConnected}
      clientActivityBetweenSessionsComponent={
        isCABSCardMovedToClientActivityTab
          ? noop
          : ClientInformationSessionsConnected
      }
      providerResourcesCollapsibleContainerComponent={
        ProviderResourcesCollapsibleContainer
      }
      preVisitSummaryCardComponent={PreVisitSummaryCardWrapper}
    />
  );
}
